export function compact(values) {
    return (values || []).filter((value) => value !== null && value !== undefined && value !== '')
}

export function uniqueBy(values, identity) {
    let seen = new Set()
    return (values || []).filter((value) => {
        let key = identity(value)
        if (!key || seen.has(key)) return false
        seen.add(key)
        return true
    })
}

export function normalizeMediaText(value) {
    return String(value || '')
        .normalize('NFKC')
        .trim()
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim()
}

export function toNullableNumber(value) {
    if (value === null || value === undefined || value === '') return null
    let number = Number(value)
    return Number.isFinite(number) ? number : null
}

export function formatYearRange(start, end) {
    let first = toNullableNumber(start)
    let last = toNullableNumber(end)
    if (!first) return ''
    if (!last || last === first) return String(first)
    return `${first}–${last}`
}

const MEDIA_KINDS = new Set(['anime', 'movie', 'series'])
const MEDIA_ID_SOURCES = new Set(['kinopoisk', 'imdb', 'tmdb', 'shikimori', 'provider'])

function usableId(value) {
    if (value === null || value === undefined) return ''
    let id = String(value).trim()
    return id && id !== '0' && id !== 'null' && id !== 'undefined' ? id : ''
}

/* The URL is keyed by a stable external ID whenever the source gives us one.
   provider/providerId remain only a fast-path hint. A replacement adapter can
   resolve the same route after the original balancer disappears. */
export function mediaReference(media) {
    let kind = MEDIA_KINDS.has(media && media.kind) ? media.kind : 'movie'
    let ids = media && media.ids || {}
    let preferredSources = kind === 'anime'
        ? ['shikimori', 'kinopoisk', 'imdb', 'tmdb']
        : ['kinopoisk', 'imdb', 'tmdb']
    let source = preferredSources.find((name) => usableId(ids[name])) || 'provider'
    let id = source === 'provider' ? usableId(media && media.id) : usableId(ids[source])

    return {
        kind,
        source,
        id,
        provider: usableId(media && media.provider),
        providerId: usableId(media && media.id),
    }
}

export function normalizeMediaReference(value = {}) {
    if (!value.source && value.provider && value.id) {
        return {
            kind: MEDIA_KINDS.has(value.kind) ? value.kind : 'movie',
            source: 'provider',
            id: usableId(value.id),
            provider: usableId(value.provider),
            providerId: usableId(value.id),
            ...(value.snapshot ? { snapshot: value.snapshot } : {}),
        }
    }

    let source = MEDIA_ID_SOURCES.has(value.source) ? value.source : 'provider'
    return {
        kind: MEDIA_KINDS.has(value.kind) ? value.kind : 'movie',
        source,
        id: usableId(value.id),
        provider: usableId(value.provider),
        providerId: usableId(value.providerId),
        ...(value.snapshot ? { snapshot: value.snapshot } : {}),
    }
}

// Routes deliberately stay short. Recover the other external identities from
// saved library/search metadata without asking a retired provider to map them.
export function mediaSnapshotForReference(reference, mediaItems = []) {
    return mediaItems.find((media) => {
        if (!media || media.kind !== reference.kind) return false
        if (reference.source === 'provider') {
            return media.provider === reference.provider && String(media.id) === reference.id
        }
        return usableId(media.ids && media.ids[reference.source]) === reference.id
    }) || null
}

export function mediaReferenceKey(value) {
    let reference = normalizeMediaReference(value)
    if (reference.source === 'provider') {
        return `${reference.kind}:provider:${reference.provider || 'unknown'}:${reference.id}`
    }
    return `${reference.kind}:${reference.source}:${reference.id}`
}

export function mediaIdentityKey(media) {
    let reference = mediaReference(media)
    return reference.id
        ? mediaReferenceKey(reference)
        : `${media && media.provider}:${media && media.id}`
}

export function mediaRoute(media, name = 'MediaView') {
    let reference = mediaReference(media)
    return {
        name,
        params: {
            kind: reference.kind,
            source: reference.source,
            id: reference.id,
        },
        query: {
            p: reference.provider || undefined,
            pid: reference.providerId || undefined,
        },
    }
}

export function mediaReferenceFromRoute(route) {
    return normalizeMediaReference({
        kind: route && route.params && route.params.kind,
        source: route && route.params && route.params.source,
        id: route && route.params && route.params.id,
        provider: route && route.query && route.query.p,
        providerId: route && route.query && route.query.pid,
    })
}
